import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Loader2, Download } from 'lucide-react';

// Custom hooks
import { useInvoices } from '../hooks/useInvoices';

// Components
import { InvoicesTable, InvoicesFilters, ManageServicesDialog } from '../components/invoices';
import InvoiceForm from '../components/InvoiceForm';
import EditInvoiceDialog from '../components/EditInvoiceDialog';
import InvoiceViewDialog from '../components/InvoiceViewDialog';
import PaymentModeDialog from '../components/PaymentModeDialog';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const Invoices = () => {
  const { hasPermission } = useAuth();
  const canDeleteInvoice = hasPermission('canDeleteInvoice');
  
  // Use custom hook for data and operations
  const {
    invoices,
    services,
    loading,
    statusFilter,
    setStatusFilter,
    sortBy,
    sortOrder,
    handleSort,
    fetchInvoices,
    handleTogglePaid,
    handleConfirmPayment,
    handleDeleteInvoice,
    handleCreateService,
    handleUpdateService,
    handleDeleteService,
    exportToExcel
  } = useInvoices();

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [serviceDialog, setServiceDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [invoiceToPay, setInvoiceToPay] = useState(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [selectedPaidDate, setSelectedPaidDate] = useState('');
  const [selectedPaymentMemo, setSelectedPaymentMemo] = useState('');
  const [selectedPaymentAmount, setSelectedPaymentAmount] = useState('');

  // Dialog handlers
  const openPaymentDialog = (invoice) => {
    setInvoiceToPay(invoice);
    setSelectedPaymentMode('');
    setSelectedPaidDate(new Date().toISOString().split('T')[0]);
    setSelectedPaymentMemo('');
    // Default to remaining balance
    const remaining = (invoice.grandTotal || 0) - (invoice.paidAmount || 0);
    setSelectedPaymentAmount(remaining);
    setPaymentDialog(true);
  };

  const onTogglePaid = (invoice) => {
    handleTogglePaid(invoice, openPaymentDialog);
  };

  const onConfirmPayment = async () => {
    const success = await handleConfirmPayment(
      invoiceToPay,
      selectedPaymentMode,
      selectedPaidDate,
      selectedPaymentMemo,
      selectedPaymentAmount,
      () => {
        setPaymentDialog(false);
        setInvoiceToPay(null);
        setSelectedPaymentMode('');
        setSelectedPaidDate('');
        setSelectedPaymentMemo('');
        setSelectedPaymentAmount('');
      }
    );
  };

  const onView = (invoice) => {
    setViewingInvoice(invoice);
    setViewDialog(true);
  };

  const onEdit = (invoice) => {
    setEditingInvoice(invoice);
    setEditDialog(true);
  };

  const onDelete = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialog(true);
  };

  const onConfirmDelete = () => {
    handleDeleteInvoice(invoiceToDelete, () => {
      setDeleteDialog(false);
      setInvoiceToDelete(null);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-500 mt-1">Manage client invoices and payments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToExcel} className="gap-2" data-testid="export-invoices-btn">
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
          <Button onClick={() => setCreateDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Filters */}
      <InvoicesFilters 
        statusFilter={statusFilter} 
        onFilterChange={setStatusFilter} 
      />

      {/* Invoices Table */}
      <InvoicesTable
        invoices={invoices}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onTogglePaid={onTogglePaid}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        canDeleteInvoice={canDeleteInvoice}
      />

      {/* Create Invoice Dialog */}
      <InvoiceForm
        open={createDialog}
        onOpenChange={setCreateDialog}
        onSuccess={fetchInvoices}
      />

      {/* Edit Invoice Dialog */}
      <EditInvoiceDialog
        open={editDialog}
        onOpenChange={(open) => {
          setEditDialog(open);
          if (!open) setEditingInvoice(null);
        }}
        invoice={editingInvoice}
        onSuccess={fetchInvoices}
      />

      {/* View Invoice Dialog */}
      <InvoiceViewDialog
        open={viewDialog}
        onOpenChange={setViewDialog}
        invoice={viewingInvoice}
      />

      {/* Manage Services Dialog */}
      <ManageServicesDialog
        open={serviceDialog}
        onOpenChange={setServiceDialog}
        services={services}
        onCreateService={(service) => handleCreateService(service, () => {})}
        onUpdateService={(service) => handleUpdateService(service, () => {})}
        onDeleteService={handleDeleteService}
      />

      {/* Delete Invoice Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        title="Delete Invoice"
        itemName={`invoice ${invoiceToDelete?.invoiceNumber}`}
        onConfirm={onConfirmDelete}
      />

      {/* Payment Mode Dialog */}
      <PaymentModeDialog
        open={paymentDialog}
        onOpenChange={setPaymentDialog}
        invoice={invoiceToPay}
        paymentMode={selectedPaymentMode}
        onPaymentModeChange={setSelectedPaymentMode}
        paidDate={selectedPaidDate}
        onPaidDateChange={setSelectedPaidDate}
        paymentMemo={selectedPaymentMemo}
        onPaymentMemoChange={setSelectedPaymentMemo}
        paymentAmount={selectedPaymentAmount}
        onPaymentAmountChange={setSelectedPaymentAmount}
        onConfirm={onConfirmPayment}
      />
    </div>
  );
};

export default Invoices;
